import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { createApiClient, ApiClient } from '../api/client';
import { GotchaUser } from '../types';
import { GotchaThemeConfig } from '../theme/tokens';
import { resolveTheme } from '../theme/resolveTheme';
import { injectStyles } from '../theme/styles';

export interface GotchaProviderProps {
  /** Your Gotcha API key */
  apiKey: string;
  /** React children */
  children: React.ReactNode;
  /** Override the API base URL (for testing/staging) */
  baseUrl?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Disable all Gotcha buttons globally */
  disabled?: boolean;
  /** Default user metadata applied to all submissions */
  defaultUser?: GotchaUser;
  /** Theme configuration overrides applied to all instances */
  themeConfig?: GotchaThemeConfig;
}

export interface GotchaContextValue {
  client: ApiClient;
  disabled: boolean;
  defaultUser: GotchaUser;
  debug: boolean;
  // Modal management - only one open at a time
  activeModalId: string | null;
  openModal: (elementId: string) => void;
  closeModal: () => void;
  // Theme
  themeConfig?: GotchaThemeConfig;
}

const GotchaContext = createContext<GotchaContextValue | null>(null);

const EMPTY_USER: GotchaUser = {};

export function GotchaProvider({
  apiKey,
  children,
  baseUrl,
  debug = false,
  disabled = false,
  defaultUser,
  themeConfig,
}: GotchaProviderProps) {
  const [activeModalId, setActiveModalId] = useState<string | null>(null);

  if (baseUrl && !baseUrl.startsWith('https://') && !baseUrl.startsWith('/') && !baseUrl.includes('localhost')) {
    console.warn('[Gotcha] baseUrl should use HTTPS in production:', baseUrl);
  }

  const client = useMemo(
    () => createApiClient({ apiKey, baseUrl, debug }),
    [apiKey, baseUrl, debug]
  );

  // Stabilize defaultUser reference
  const stableDefaultUser = useMemo(
    () => defaultUser ?? EMPTY_USER,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(defaultUser)]
  );

  // Inject styles on mount. Carter One @font-face is injected at module-load time
  // (styles.ts:37) so it's already available before this runs.
  useEffect(() => {
    const resolved = resolveTheme('light', 'light', themeConfig);
    injectStyles(resolved);
  }, [themeConfig]);

  const openModal = useCallback((elementId: string) => {
    setActiveModalId(elementId);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModalId(null);
  }, []);

  const value: GotchaContextValue = useMemo(
    () => ({
      client,
      disabled,
      defaultUser: stableDefaultUser,
      debug,
      activeModalId,
      openModal,
      closeModal,
      themeConfig,
    }),
    [client, disabled, stableDefaultUser, debug, activeModalId, openModal, closeModal, themeConfig]
  );

  return (
    <GotchaContext.Provider value={value}>{children}</GotchaContext.Provider>
  );
}

export function useGotchaContext(): GotchaContextValue {
  const context = useContext(GotchaContext);
  if (!context) {
    throw new Error('useGotchaContext must be used within a GotchaProvider');
  }
  return context;
}
